/* charger, going away — the rump, on the same eight beats.
 *
 * THE TELL IS WHAT IS MISSING. Facing away there are no eyes, and the horns
 * are on the far side of a body that hides them: only the tips clear the
 * withers, and they clear less of it the further the head goes down. Measured
 * as the horn tips' outermost lit column, 3/4/gone/1/1/1/4/5 — they vanish
 * entirely at the bottom of the coil, which is the frame a player most needs
 * to read, and they are out at the cell edge through the whole rush. A rear
 * view that showed a face would be worse than no rear view.
 *
 * WHAT REPLACES THE FACE. The `r` bands run across the back the same way they
 * run across the flank — a striped animal is striped from here too, and
 * repainting them would only have made this look like a different creature.
 * What is new is a two-pixel `o` notch at columns 7-8 of the two lowest body
 * rows: the tail, the one part of a charger that is only ever seen from here.
 * The silhouette is deliberately `charger_down`'s, width for width; a body
 * that changed shape when it turned would read as a swap, not a turn. What a
 * player reads at 16px is bone at the top and eyes or none.
 *
 * The phases, the floor rule and the mass rule are `charger.ts`'s; see the
 * note there. Legs are filled `R` for the reason `charger_down` records.
 *
 * Measured: steps 38/60/36/68/82/76/60/38 px, spread 1.4x the median and 2.3x
 * the smallest, mass 5.6% about a median of 106, floor drift 1 row.
 */

// phase 0 — SET. Standing square, tail hanging straight, both horn tips
// showing two pixels of bone over the withers.
export const charger_up_0 = [
  '................',
  '................',
  '................',
  '................',
  '...oo......oo...',
  '...ou......uo...',
  '..oooooooooooo..',
  '..oRRRRRRRRRRo..',
  '..oRRrrrrrrRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRooRRRRRo.',
  '.oRRRRRooRRRRRo.',
  '..RR.RR..RR.RR..',
  '................',
  '................',
];

// phase 1 — LOAD. The head goes down and the tips shorten to one pixel each;
// the withers bunch and the spine stripe drops a row with the back.
export const charger_up_1 = [
  '................',
  '................',
  '................',
  '................',
  '....o......o....',
  '...ou......uo...',
  '..oooooooooooo..',
  '..ooRRRRRRRRoo..',
  '..oRRRRRRRRRRo..',
  '..oRRrrrrrrRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRooRRRRRo.',
  '.RRR.RR..RR.RRR.',
  '................',
  '................',
];

// phase 2 — COIL. The head is gone behind the shoulders — no bone at all —
// and the body is out to both cell edges. The one frame with nothing to face.
export const charger_up_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....oooooo.....',
  '...oooooooooo...',
  '..oRRRRRRRRRRo..',
  '..oRRrrrrrrRRo..',
  '.oRRRRRRRRRRRRo.',
  'ooRRrrrrrrrrRRoo',
  'ooRRRRRRRRRRRRoo',
  'ooRRRRRooRRRRRoo',
  '..RR.RR..RR.RR..',
  '................',
  '................',
];

// phase 3 — UNLOAD. The horns swing wide and reappear past the shoulders at
// the cell edge; the outer legs drive below row 13 and the inner pair folds.
export const charger_up_3 = [
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ou..........uo.',
  '.oooooooooooooo.',
  '..oRRRRRRRRRRo..',
  '..oRRrrrrrrRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRooRRRRRo.',
  '.oRRRRRooRRRRRo.',
  '..RR..R..R..RR..',
  '..RR........RR..',
  '................',
];

// phase 4 — DRIVE. Everything pushing one way: the haunch narrows to twelve
// around the tail and all four feet are on their toes.
export const charger_up_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ou..........uo.',
  '.oooooooooooooo.',
  '.oRRRRRRRRRRRRo.',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '..oRRRRooRRRRo..',
  '.oRRRRRooRRRRRo.',
  '..RR.RR..RR.RR..',
  '..RR.R....R.RR..',
  '................',
];

// phase 5 — LAUNCH. Legs splayed widest, the tail notch riding a row higher
// than the standing pose.
export const charger_up_5 = [
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ou..........uo.',
  '.oooooooooooooo.',
  '.oRRRRRRRRRRRRo.',
  '..oRRrrrrrrRRo..',
  '.oRRRRRRRRRRRRo.',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRooRRRRRo.',
  '.RR..RR..RR..RR.',
  'RR...RR..RR...RR',
  '................',
  '................',
];

// phase 6 — STRIKE. The rump drops onto two wide blocks of foot and the horn
// tips shorten again as the head goes back down.
export const charger_up_6 = [
  '................',
  '................',
  '................',
  '................',
  '....o......o....',
  '...ou......uo...',
  '..oooooooooooo..',
  '..ooRRRRRRRRoo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '..oRRRRRRRRRRo..',
  '.oRRRRRooRRRRRo.',
  '.oRRRRRooRRRRRo.',
  '..RRR......RRR..',
  '..RRR......RRR..',
  '................',
];

// phase 7 — CATCH. Gathered: horn tips drawn right in over the crown, the
// back narrow, the legs passing under. Next: set.
export const charger_up_7 = [
  '................',
  '................',
  '................',
  '................',
  '.....oo..oo.....',
  '.....ou..uo.....',
  '..oooooooooooo..',
  '...oRRRRRRRRo...',
  '...oRRrrrrRRo...',
  '.oRRRRRRRRRRRRo.',
  'ooRRrrrrrrrrRRoo',
  'ooRRRRRooRRRRRoo',
  '.oRRRRRooRRRRRo.',
  '...RR.RR.RR.RR..',
  '................',
  '................',
];
